import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { api } from "../lib/api/client";
import { tenantPath, useTenantSlug } from "../lib/tenant";
import { useVisualizer } from "../lib/state/visualizerStore";
import { useBranding } from "../lib/state/brandingContext";
import { AppShell, card } from "../components/AppShell";
import { Button } from "../components/Button";
import { Spinner } from "../components/Spinner";

/**
 * The postcard QR landing (spec §6): `/c/{code}` (subdomain mode) or `/{tenantSlug}/c/{code}`
 * (path mode). Fetches the personalization payload, seeds the store (photo + detections + hero
 * preset on every door — no upload, no re-extraction) and drops the homeowner straight into the
 * editor. Only a definitive 404 (the server's link_expired shape) renders the branded expired
 * card; a transient failure — flaky mobile network, a 429 from the shared rate window — offers
 * a retry instead of falsely declaring a live link dead.
 */
export function PostcardLandingPage() {
  const { code = "" } = useParams();
  const tenantSlug = useTenantSlug();
  const navigate = useNavigate();
  const initFromPersonalization = useVisualizer((s) => s.initFromPersonalization);
  const { branding } = useBranding();
  const [state, setState] = useState<"loading" | "expired" | "error">("loading");
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setState("loading");
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .getPostcardLanding(tenantSlug, code)
      .then((payload) => {
        if (cancelled) return;
        initFromPersonalization(code, payload);
        navigate(tenantPath(tenantSlug, "visualize"), { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        setState(isAxiosError(err) && err.response?.status === 404 ? "expired" : "error");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, code, attempt]);

  if (state === "loading") {
    return (
      <AppShell step="select">
        <div style={{ ...card, textAlign: "center", maxWidth: 460, margin: "40px auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <Spinner />
          <p style={{ color: "var(--muted)", margin: 0 }}>Preparing your personalized preview…</p>
        </div>
      </AppShell>
    );
  }

  if (state === "error") {
    return (
      <AppShell step="select">
        <div data-testid="postcard-retry" style={{ ...card, textAlign: "center", maxWidth: 460, margin: "40px auto" }}>
          <h2 style={{ margin: "0 0 6px" }}>We couldn't load your preview</h2>
          <p style={{ color: "var(--muted)", marginTop: 0 }}>
            Something hiccuped on the way — your personalized link is still good.
          </p>
          <Button onClick={retry}>Try again</Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell step="select">
      <div data-testid="postcard-expired" style={{ ...card, textAlign: "center", maxWidth: 460, margin: "40px auto" }}>
        {branding?.logoUrl && (
          <img src={branding.logoUrl} alt="" style={{ height: 36, objectFit: "contain", marginBottom: 12 }} />
        )}
        <h2 style={{ margin: "0 0 6px" }}>This link has expired</h2>
        <p style={{ color: "var(--muted)", marginTop: 0 }}>
          The personalized preview on your postcard is no longer available — but you can still
          design your new garage door from a photo of your home.
        </p>
        <Button onClick={() => navigate(tenantPath(tenantSlug))}>Try it on your home</Button>
      </div>
    </AppShell>
  );
}
